import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CalendarIcon, Send, Save, Users, Plus, X } from "lucide-react";
import { format } from "date-fns";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";

export const CampaignBuilder = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<"email" | "sms" | "both">("email");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [segmentType, setSegmentType] = useState<"all" | "customers" | "crm" | "manual">("all");
  const [scheduledDate, setScheduledDate] = useState<Date>();
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [manualEmails, setManualEmails] = useState<string[]>([]);
  const [manualPhones, setManualPhones] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [selectedCrmContacts, setSelectedCrmContacts] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: templates } = useQuery({
    queryKey: ["campaign-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaign_templates")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: crmContacts } = useQuery({
    queryKey: ["crm-contacts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crm_contacts")
        .select("id, customer_id, customers(name, email, phone)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: segmentType === "crm",
  });

  const { data: allCustomers } = useQuery({
    queryKey: ["all-customers"],
    queryFn: async () => {
      // Fetch manual customers
      const { data: customers, error: customersError } = await supabase
        .from("customers")
        .select("id, name, email, phone")
        .order("created_at", { ascending: false });
      if (customersError) throw customersError;

      // Fetch client portal users
      const { data: clientProfiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, email")
        .eq("user_type", "client")
        .order("created_at", { ascending: false });
      if (profilesError) throw profilesError;

      // Combine both sources
      const combinedContacts = [
        ...(customers || []),
        ...(clientProfiles || []).map((profile: any) => ({
          id: profile.id,
          name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email,
          email: profile.email,
          phone: null, // Client portal users don't have phone in profiles
        }))
      ];

      return combinedContacts;
    },
    enabled: segmentType === "customers",
  });

  const { data: recipientCount } = useQuery({
    queryKey: ["recipient-count", segmentType, selectedCrmContacts, manualEmails, manualPhones, allCustomers],
    queryFn: async () => {
      if (segmentType === "all") {
        // Count both customers and client portal users (matching send-campaign logic)
        const { count: customerCount } = await supabase
          .from("customers")
          .select("*", { count: "exact", head: true });
        
        const { count: clientCount } = await supabase
          .from("profiles")
          .select("*", { count: "exact", head: true })
          .eq("user_type", "client");
        
        return (customerCount || 0) + (clientCount || 0);
      } else if (segmentType === "customers") {
        // Count selected customers
        return selectedCrmContacts.length > 0 ? selectedCrmContacts.length : 0;
      } else if (segmentType === "crm") {
        return selectedCrmContacts.length;
      } else if (segmentType === "manual") {
        return Math.max(manualEmails.length, manualPhones.length);
      }
      return 0;
    },
  });

  const createCampaignMutation = useMutation({
    mutationFn: async (status: "draft" | "scheduled" | "sending") => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const segmentFilters: any = {};
      if (segmentType === "crm") {
        segmentFilters.crm_contact_ids = selectedCrmContacts;
      } else if (segmentType === "customers") {
        // Store selected customer IDs
        segmentFilters.manual_emails = selectedCrmContacts;
      } else if (segmentType === "manual") {
        segmentFilters.manual_emails = manualEmails;
        segmentFilters.manual_phones = manualPhones;
      }

      const { data, error } = await supabase
        .from("campaigns")
        .insert([{
          user_id: user.id,
          name,
          description,
          type,
          subject: type === "email" || type === "both" ? subject : null,
          message,
          segment_type: segmentType,
          segment_filters: segmentFilters,
          recipient_count: recipientCount || 0,
          status,
          scheduled_at: scheduledDate?.toISOString(),
        }])
        .select()
        .single();

      if (error) throw error;

      // If sending immediately, trigger the send-campaign function
      if (status === "sending") {
        const { data: sendData, error: sendError } = await supabase.functions.invoke("send-campaign", {
          body: { campaignId: data.id },
        });
        
        if (sendError) {
          console.error("Error sending campaign:", sendError);
          throw sendError;
        }
        
        return { ...data, sendResult: sendData };
      }

      return data;
    },
    onSuccess: (data: any, status) => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      
      if (status === "sending" && data?.sendResult) {
        toast.success(
          `Campaign sent! ${data.sendResult.emailsSent || 0} emails, ${data.sendResult.smsSent || 0} SMS`
        );
      } else if (status === "draft") {
        toast.success("Campaign saved as draft");
      } else if (status === "scheduled") {
        toast.success("Campaign scheduled successfully");
      }
      
      resetForm();
    },
    onError: (error: any) => {
      console.error("Error creating campaign:", error);
      toast.error("Failed to create campaign");
    },
  });

  const resetForm = () => {
    setName("");
    setDescription("");
    setSubject("");
    setMessage("");
    setSegmentType("all");
    setScheduledDate(undefined);
    setSelectedTemplate("");
    setManualEmails([]);
    setManualPhones([]);
    setSelectedCrmContacts([]);
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates?.find(t => t.id === templateId);
    if (template) {
      setType(template.type as "email" | "sms" | "both");
      setSubject(template.subject || "");
      setMessage(template.message);
      setSelectedTemplate(templateId);
      toast.success("Template loaded");
    }
  };

  const addManualEmail = () => {
    if (emailInput && emailInput.includes("@")) {
      setManualEmails([...manualEmails, emailInput]);
      setEmailInput("");
    } else {
      toast.error("Please enter a valid email");
    }
  };

  const addManualPhone = () => {
    if (phoneInput) {
      setManualPhones([...manualPhones, phoneInput]);
      setPhoneInput("");
    } else {
      toast.error("Please enter a phone number");
    }
  };

  const toggleCrmContact = (contactId: string) => {
    setSelectedCrmContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create Campaign</CardTitle>
        <CardDescription>
          Build and send marketing campaigns to your audience
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Campaign Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Summer Sale 2024"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="type">Campaign Type</Label>
            <Select value={type} onValueChange={(v: any) => setType(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="both">Email + SMS</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Input
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of this campaign"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="template">Use Template (Optional)</Label>
          <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Select a template" />
            </SelectTrigger>
            <SelectContent>
              {templates?.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name} - {template.category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {(type === "email" || type === "both") && (
          <div className="space-y-2">
            <Label htmlFor="subject">Email Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Your subject line"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="message">Message</Label>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Your message content... Use {{variable_name}} for personalization"
            rows={8}
          />
          <p className="text-xs text-muted-foreground">
            Available variables: {"{{customer_name}}"}, {"{{company_name}}"}, {"{{project_name}}"}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="segment">Target Audience</Label>
          <Select value={segmentType} onValueChange={(v: any) => setSegmentType(v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <span>All Contacts (Everyone)</span>
                </div>
              </SelectItem>
              <SelectItem value="customers">Select Specific Customers</SelectItem>
              <SelectItem value="crm">Select from CRM</SelectItem>
              <SelectItem value="manual">Manual Input</SelectItem>
            </SelectContent>
          </Select>
          
          {segmentType === "all" && (
            <div className="rounded-md bg-blue-50 dark:bg-blue-950 p-3 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <Users className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                    📧 Sending to ALL contacts automatically
                  </p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    This will send to every customer in your database + all client portal users. 
                    Expected recipients: <strong>{recipientCount || 0}</strong>
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {segmentType === "customers" && (
            <p className="text-xs text-muted-foreground">
              Select specific customers from your list to send targeted campaigns
            </p>
          )}
          
          {segmentType === "crm" && (
            <p className="text-xs text-muted-foreground">
              Select contacts from your CRM system
            </p>
          )}
          
          {segmentType === "manual" && (
            <p className="text-xs text-muted-foreground">
              Add email addresses and phone numbers manually
            </p>
          )}
        </div>

        {segmentType === "customers" && (
          <div className="space-y-2">
            <Label>Select Customers ({allCustomers?.length || 0} available)</Label>
            {!allCustomers || allCustomers.length === 0 ? (
              <p className="text-sm text-muted-foreground border rounded-md p-4">
                No customers found. Add customers in the Clients section first.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="border rounded-md p-4 max-h-60 overflow-y-auto space-y-3">
                  {allCustomers.map((customer: any) => (
                    <div key={customer.id} className="flex items-start space-x-3 p-2 hover:bg-accent rounded-md">
                      <Checkbox
                        checked={selectedCrmContacts.includes(customer.id)}
                        onCheckedChange={() => toggleCrmContact(customer.id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <label className="text-sm font-medium cursor-pointer block">
                          {customer.name}
                        </label>
                        <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                          {customer.email && (
                            <div className="flex items-center gap-1">
                              <span className="font-medium">Email:</span>
                              <span>{customer.email}</span>
                            </div>
                          )}
                          {customer.phone && (
                            <div className="flex items-center gap-1">
                              <span className="font-medium">Phone:</span>
                              <span>{customer.phone}</span>
                            </div>
                          )}
                          {!customer.email && !customer.phone && (
                            <span className="text-destructive">⚠️ No contact info</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  💡 Tip: Customers without email or phone won't receive the campaign
                </p>
              </div>
            )}
          </div>
        )}

        {segmentType === "crm" && (
          <div className="space-y-2">
            <Label>Select CRM Contacts ({crmContacts?.length || 0} available)</Label>
            {!crmContacts || crmContacts.length === 0 ? (
              <p className="text-sm text-muted-foreground border rounded-md p-4">
                No CRM contacts found. Add contacts in the CRM section first, or use "Select from Customers" instead.
              </p>
            ) : (
              <div className="border rounded-md p-4 max-h-60 overflow-y-auto space-y-2">
                {crmContacts.map((contact: any) => (
                  <div key={contact.id} className="flex items-center space-x-2">
                    <Checkbox
                      checked={selectedCrmContacts.includes(contact.id)}
                      onCheckedChange={() => toggleCrmContact(contact.id)}
                    />
                    <label className="text-sm flex-1 cursor-pointer">
                      {contact.customers?.name || "No name"} - {contact.customers?.email || contact.customers?.phone || "No contact info"}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {segmentType === "manual" && (
          <div className="space-y-4">
            {(type === "email" || type === "both") && (
              <div className="space-y-2">
                <Label>Add Email Addresses</Label>
                <div className="flex gap-2">
                  <Input
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="email@example.com"
                    onKeyPress={(e) => e.key === "Enter" && addManualEmail()}
                  />
                  <Button type="button" onClick={addManualEmail} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {manualEmails.map((email, idx) => (
                    <Badge key={idx} variant="secondary">
                      {email}
                      <X
                        className="ml-2 h-3 w-3 cursor-pointer"
                        onClick={() => setManualEmails(manualEmails.filter((_, i) => i !== idx))}
                      />
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {(type === "sms" || type === "both") && (
              <div className="space-y-2">
                <Label>Add Phone Numbers</Label>
                <div className="flex gap-2">
                  <Input
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    placeholder="+1234567890"
                    onKeyPress={(e) => e.key === "Enter" && addManualPhone()}
                  />
                  <Button type="button" onClick={addManualPhone} size="sm">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {manualPhones.map((phone, idx) => (
                    <Badge key={idx} variant="secondary">
                      {phone}
                      <X
                        className="ml-2 h-3 w-3 cursor-pointer"
                        onClick={() => setManualPhones(manualPhones.filter((_, i) => i !== idx))}
                      />
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {recipientCount !== undefined && (
          <div className="rounded-md bg-muted/50 p-3 border">
            <p className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <strong>{recipientCount}</strong> recipient{recipientCount !== 1 ? 's' : ''} will receive this campaign
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>Schedule (Optional)</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {scheduledDate ? format(scheduledDate, "PPP") : "Send immediately"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={scheduledDate}
                onSelect={setScheduledDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            onClick={() => createCampaignMutation.mutate("draft")}
            variant="outline"
            disabled={!name || !message || createCampaignMutation.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
          <Button
            onClick={() => createCampaignMutation.mutate(scheduledDate ? "scheduled" : "sending")}
            disabled={!name || !message || createCampaignMutation.isPending}
          >
            <Send className="mr-2 h-4 w-4" />
            {scheduledDate ? "Schedule Campaign" : `Send Now (${recipientCount || 0})`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
